import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { Card, DraggableGrid, Typography, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useActiveServiceTemplates } from '~/hooks/reactQuery/queries/useActiveServiceTemplates';
import { useLocaleKeys } from '~/i18n';
import { ServiceRow } from './ServiceRow';
import s from './ServicesCard.module.scss';
export const serviceRowTestId = (cardTestId, index)=>"".concat(cardTestId !== null && cardTestId !== void 0 ? cardTestId : '', "-row-").concat(index);
export const GetServicesCardTID = (parentTestId)=>getTestIds(parentTestId);
const DraggableServiceCard = (param)=>{
    let { renderService, isDragActive, dragHandle } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: renderService(isDragActive, dragHandle)
    });
};
const stableServiceId = (service)=>{
    const matchedId = service.matchStatus === 'matched' ? service.matchedServiceTemplateId : 'unmatched';
    return "".concat(service.name, "-").concat(matchedId);
};
export const ServicesCard = (param)=>{
    let { extraction, setExtraction, className, testId } = param;
    const { ServicesCardTestIds } = useMemo(()=>GetServicesCardTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { isMobileDevice } = useDevice();
    const { data: serviceTemplates, isLoading, error } = useActiveServiceTemplates();
    const services = t.agreementImport.triageModal.services;
    const models = useMemo(()=>extraction.serviceModels(), [
        extraction
    ]);
    const rawServices = useMemo(()=>extraction.services(), [
        extraction
    ]);
    const isDraggable = !isMobileDevice && models.length >= 2;
    const handleReorder = useCallback((reorderedItems)=>{
        const reordered = reorderedItems.map((item)=>rawServices.find((svc)=>stableServiceId(svc) === item.id)).filter((svc)=>svc != null);
        setExtraction(extraction.reorderServices(reordered));
    }, [
        extraction,
        rawServices,
        setExtraction
    ]);
    const renderRow = (model, index, isDragActive, dragHandle)=>{
        const dto = model.dto();
        const matchedId = dto.matchStatus === 'matched' ? dto.matchedServiceTemplateId : undefined;
        const foundTemplate = matchedId != null ? serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.find((tpl)=>tpl.id === matchedId) : undefined;
        const templateLookupFailed = matchedId != null && serviceTemplates != null && foundTemplate == null;
        return /*#__PURE__*/ _jsx(Card, {
            bi: {
                name: 'agreement_triage_service_item_card'
            },
            padding: isMobileDevice ? 's' : 'm',
            skin: "white",
            borderSkin: "default",
            borderRadius: "m",
            className: s.serviceItemCard,
            children: /*#__PURE__*/ _jsx(ServiceRow, {
                testId: serviceRowTestId(testId, index),
                service: model,
                isDragActive: isDragActive,
                dragHandle: dragHandle,
                onRemove: ()=>setExtraction(extraction.removeService(index)),
                onUseTemplate: ()=>{
                    if (foundTemplate != null) {
                        setExtraction(extraction.replaceServiceWithTemplate(index, foundTemplate));
                    }
                },
                onUseFileVersion: ()=>setExtraction(extraction.switchServiceToFileVersion(index)),
                isTemplateActionLoading: isLoading,
                isTemplateActionDisabled: error != null || templateLookupFailed
            })
        });
    };
    return /*#__PURE__*/ _jsxs(Card, {
        bi: {
            name: 'agreement_triage_services_card'
        },
        padding: isMobileDevice ? 's' : 'm',
        borderRadius: "m",
        testId: ServicesCardTestIds.Root(),
        className: cn(s.card, className),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: s.cardHeader,
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    weight: "normal",
                    testId: ServicesCardTestIds.Title(),
                    children: services.title()
                })
            }),
            isDraggable ? /*#__PURE__*/ _jsx(DraggableGrid, {
                bi: {
                    name: 'reorder_triage_service'
                },
                items: models.map((model, idx)=>({
                        id: stableServiceId(rawServices[idx]),
                        renderService: (isDragActive, dragHandle)=>renderRow(model, idx, isDragActive, dragHandle)
                    })),
                itemComponent: DraggableServiceCard,
                onReorder: handleReorder,
                dragMode: "withGhost",
                layout: {
                    columns: 1
                },
                itemDragActivation: "handle",
                itemHandlePosition: "inside",
                gap: 16,
                testId: "triage-services-grid"
            }) : models.map((model, idx)=>{
                const stableKey = stableServiceId(rawServices[idx]);
                return /*#__PURE__*/ _jsx("div", {
                    children: renderRow(model, idx)
                }, stableKey);
            })
        ]
    });
};
